import React, { useState } from "react"
import { makeStyles } from "@material-ui/core/styles"
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Tooltip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  TextField,
} from "@material-ui/core"
import moment from "moment"
import {
  Code as CodeIcon,
  Schedule as ScheduleIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  FileCopy as CloneIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@material-ui/icons"
import CustomCheckbox from "../../form-components/custom-checkbox"
import useStore from "src/v2/utils/store"
import { useAlertStore } from "src/v2/utils/alert-store"
import PassFailChips from "../../../../components/pass-fail-chips" // Add this import

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
    display: "flex",
    flexDirection: "column",
    transition: "all 0.3s ease",
    cursor: "pointer",
    height: 140,
    width: "100%",
    position: "relative",
    overflow: "hidden",
    border: "1px solid #f0f0f0",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      borderColor: theme.palette.primary.light,
      "& $checkboxContainer": {
        opacity: 1,
        visibility: "visible",
      },
    },
    backgroundColor: "#ffffff",
    [theme.breakpoints.down("sm")]: {
      height: "auto",
      minHeight: 160,
    },
  },
  selectedCard: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light + "10", // Very light primary color
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: theme.spacing(1.5, 2, 1, 2),
    borderBottom: "1px solid #f5f5f5",
    position: "relative",
    width: "100%", // Ensure header takes full width
  },
  title: {
    fontWeight: 600,
    fontSize: "1rem",
    flex: 1,
    wordBreak: "break-word",
    display: "-webkit-box",
    "-webkit-line-clamp": 1,
    "-webkit-box-orient": "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.4,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.primary,
    paddingLeft: theme.spacing(3), // Space for checkbox
    paddingRight: theme.spacing(12), // Increased space for pass/fail chips + menu button
    maxWidth: "calc(100% - 140px)", // Increased to accommodate chips
    [theme.breakpoints.down("sm")]: {
      paddingRight: theme.spacing(10),
      maxWidth: "calc(100% - 120px)",
    },
  },
  contentContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    height: "100%",
    flex: 1,
    padding: theme.spacing(1, 2),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column", // Stack vertically on mobile
      padding: theme.spacing(1),
    },
  },
  metaContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    flex: 1,
    minWidth: 0, // Prevent flex children from overflowing
    marginRight: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      marginRight: 0,
      marginBottom: theme.spacing(1),
    },
  },
  meta: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: 13,
    flexWrap: "wrap", // Allow wrapping on small screens
    minWidth: 0, // Prevent overflow
  },
  chip: {
    marginLeft: theme.spacing(1),
    fontWeight: 500,
    height: 22,
    maxWidth: "100%", // Prevent overflow
  },
  privateChip: {
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.secondary.dark,
  },
  stepsChip: {
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.primary.dark,
    [theme.breakpoints.down("xs")]: {
      marginTop: theme.spacing(0.5),
      marginLeft: 0,
    },
  },
  functionsContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    justifyContent: "center",
    maxWidth: "40%", // Reduced from 50% to prevent overflow
    minWidth: 0, // Prevent flex children from overflowing
    [theme.breakpoints.down("sm")]: {
      maxWidth: "50%", // More space on smaller screens
    },
    [theme.breakpoints.down("xs")]: {
      maxWidth: "100%", // Full width on mobile
      alignItems: "flex-start", // Left align on mobile
      marginTop: theme.spacing(1),
      borderTop: "1px solid #f5f5f5",
      paddingTop: theme.spacing(1),
    },
  },
  tagsRow: {
    display: "flex",
    flexWrap: "nowrap",
    overflow: "hidden",
    maxWidth: "100%",
    [theme.breakpoints.down("xs")]: {
      flexWrap: "wrap", // Allow wrapping on mobile
    },
  },
  tag: {
    marginLeft: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    backgroundColor: theme.palette.primary.light + "30",
    color: theme.palette.primary.dark,
    fontWeight: 500,
    height: 22,
    borderRadius: 11,
    fontSize: "0.675rem",
    whiteSpace: "nowrap",
    maxWidth: "100%", // Prevent overflow
    [theme.breakpoints.down("xs")]: {
      marginLeft: 0,
      marginRight: theme.spacing(0.5),
    },
  },
  moreTags: {
    marginLeft: theme.spacing(0.5),
    fontSize: 12,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
  },
  icon: {
    fontSize: 16,
    marginRight: theme.spacing(0.5),
    opacity: 0.8,
    flexShrink: 0, // Prevent icon from shrinking
  },
  infoText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "0.8125rem",
    minWidth: 0, // Allow text to shrink
  },
  avatarContainer: {
    marginRight: theme.spacing(1),
    flexShrink: 0, // Prevent avatar from shrinking
  },
  avatar: {
    width: 20,
    height: 20,
    fontSize: 10,
    backgroundColor: theme.palette.primary.main,
  },
  tagsHeading: {
    fontSize: 11,
    fontWeight: 500,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    letterSpacing: 0.5,
    marginBottom: theme.spacing(0.5),
    textAlign: "right",
    [theme.breakpoints.down("xs")]: {
      textAlign: "left", // Left align on mobile
    },
  },
  checkboxContainer: {
    position: "absolute",
    top: theme.spacing(1.5),
    left: theme.spacing(1),
    opacity: 0,
    visibility: "hidden",
    transition: "opacity 0.2s ease",
    zIndex: 2,
    [theme.breakpoints.down("sm")]: {
      opacity: 1,
      visibility: "visible",
    },
  },
  checkboxVisible: {
    opacity: 1,
    visibility: "visible",
  },
  simulationId: {
    opacity: 0.8,
    fontWeight: 'normal'
  },
  // Menu button styles
  menuButton: {
    position: "absolute",
    top: theme.spacing(1.2), // Align with title better
    right: theme.spacing(1),
    zIndex: 10,
    color: theme.palette.text.secondary,
    opacity: 1,
    pointerEvents: "auto",
    transition: "color 0.2s",
    padding: 4,
    backgroundColor: "transparent",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "transparent",
      color: theme.palette.primary.main,
    },
  },
  // Add new style for pass/fail chips container
  passFailContainer: {
    position: "absolute",
    top: theme.spacing(2), // Increased from 1.5 to 2 for better alignment
    right: theme.spacing(5), // Position to the left of the menu button
    zIndex: 9,
    display: "flex",
    alignItems: "center",
    height: theme.spacing(3), // Add explicit height to match title line height
    [theme.breakpoints.down("sm")]: {
      right: theme.spacing(4), // Adjust for smaller screens
      top: theme.spacing(1.8), // Slightly adjusted for smaller screens
    },
  },
  menuItem: {
    minHeight: "auto",
    padding: theme.spacing(1, 2),
  },
  menuItemIcon: {
    marginRight: theme.spacing(1),
    fontSize: 20,
  },
  // Edit mode styles
  editContainer: {
    position: "relative",
    zIndex: 15,
    width: "100%", // Take full width
    paddingRight: theme.spacing(3), // Leave space for the menu
  },
  editInput: {
    "& .MuiInputBase-root": {
      fontSize: "1rem",
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    width: "calc(100% - 30px)", // Take full width minus menu space
    maxWidth: "100%", // Allow full width
  },
  editActions: {
    display: "flex",
    alignItems: "center",
    zIndex: 50,
  },
  actionIcon: {
    padding: 4,
  },
}))

export default function SimulationCard({
  simulation,
  onClick,
  onDelete,
  onRefresh,
  selected = false,
  onSelect,
}) {
  console.log("simulation", simulation)
  const classes = useStyles()
  const [hovering, setHovering] = useState(false)
  

  // Get store functions
  const updateSimulation = useStore((state) => state.updateSimulation)
  const showAlert = useAlertStore((state) => state.showAlert)
  // Get the duplicateSimulation function from the store
  const duplicateSimulation = useStore((state) => state.duplicateSimulation)
  
  // Edit state
  const [isEditing, setIsEditing] = useState(false)
  const [editedName, setEditedName] = useState(simulation.name || "Untitled Simulation")
  const [nameError, setNameError] = useState("")
  
  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const isMenuOpen = Boolean(menuAnchorEl)
  
  // Function to get initials for avatar
  const getInitials = (name) => {
    if (!name) return "?"
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }

  // Limit tags to display horizontally
  const MAX_DISPLAYED_TAGS = 2
  const hasMoreTags =
    simulation.functionsInUse &&
    simulation.functionsInUse.length > MAX_DISPLAYED_TAGS
  const displayedTags = simulation.functionsInUse
    ? simulation.functionsInUse.slice(0, MAX_DISPLAYED_TAGS)
    : []
  const additionalTagsCount = simulation.functionsInUse
    ? simulation.functionsInUse.length - MAX_DISPLAYED_TAGS
    : 0

  // Handle card click
  const handleCardClick = () => {
    if (!isEditing && onClick) {
      onClick();
    }
  }

  // Menu handlers
  const handleMenuOpen = (event) => {
    event.stopPropagation()
    setMenuAnchorEl(event.currentTarget)
  }
  
  const handleMenuClose = (event) => {
    if (event) event.stopPropagation()
    setMenuAnchorEl(null)
  }
  
  const handleEditClick = (event) => {
    if (event) event.stopPropagation()
    setMenuAnchorEl(null)
    setIsEditing(true)
    setEditedName(simulation.name || "Untitled Simulation")
    setNameError("")
  }
  
  const handleCloneClick = async (event) => {
    if (event) event.stopPropagation()
    setMenuAnchorEl(null)
    
    try {
      // Create a new name by appending "- Copy" to the original name
      const newName = `${simulation.name || "Untitled Simulation"} - Copy`
      
      // Call the store's duplicateSimulation function
      const result = await duplicateSimulation(simulation._id, newName)
      
      if (result) {
        showAlert({
          message: "Simulation cloned successfully",
          severity: "success",
          title: "Success",
        })
        
        // Refresh the list if onRefresh callback is provided
        if (onRefresh) {
          onRefresh()
        }
      }
    } catch (error) {
      console.error("Error cloning simulation:", error)
      showAlert({
        message: "Failed to clone simulation",
        severity: "error",
        title: "Error",
      })
    }
  }
  
  const handleDeleteClick = (event) => {
    if (event) event.stopPropagation()
    setMenuAnchorEl(null)
    if (onDelete) {
      onDelete(simulation._id)
    }
  }

  // Edit handlers
  const handleCancelEdit = (e) => {
    if (e) e.stopPropagation()
    setIsEditing(false)
    setEditedName(simulation.name || "Untitled Simulation")
    setNameError("")
  }
  
  const validateName = (name) => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      setNameError("Simulation name is required")
      return false
    }
    setNameError("")
    return true
  }
  
  const handleSaveEdit = async (e) => {
    if (e) e.stopPropagation()
    
    const trimmedName = editedName.trim()
    if (!validateName(trimmedName)) {
      return
    }
    
    try {
      // Only update if the name has changed
      if (trimmedName !== simulation.name) {
        // Call updateSimulation from the store with proper params
        await updateSimulation("name", trimmedName, simulation._id, showAlert)
        
        // Refresh simulations list if refresh callback is provided
        if (onRefresh) {
          onRefresh()
        }
      }
      
      setIsEditing(false)
    } catch (error) {
      console.error("Error updating simulation name:", error)
    }
  }
  
  const handleNameChange = (e) => {
    setEditedName(e.target.value)
    if (nameError) {
      validateName(e.target.value)
    }
  }
  
  const handleNameKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSaveEdit(e)
    } else if (e.key === "Escape") {
      handleCancelEdit(e)
    }
  }

  // Handle select checkbox click
  const handleSelectClick = (e) => {
    e.stopPropagation()
    if (onSelect) {
      onSelect(simulation._id)
    }
  }

  return (
    <Card
      className={`${classes.root} ${selected ? classes.selectedCard : ""}`}
      onClick={handleCardClick}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {/* Pass/Fail Chips - positioned to the left of menu button */}
      <Box className={classes.passFailContainer}>
        <PassFailChips pass_expections={simulation.passExpections} />
      </Box>

      {/* Menu Button */}
      <IconButton
        className={classes.menuButton}
        aria-label="more options"
        onClick={handleMenuOpen}
        size="small"
      >
        <MoreVertIcon />
      </IconButton>
      
      {/* Dropdown Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()} // Prevent card click when interacting with menu
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleEditClick} className={classes.menuItem}>
          <EditIcon className={classes.menuItemIcon} fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem onClick={handleCloneClick} className={classes.menuItem}>
          <CloneIcon className={classes.menuItemIcon} fontSize="small" />
          Clone
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} className={classes.menuItem}>
          <DeleteIcon className={classes.menuItemIcon} fontSize="small" color="error" />
          Delete
        </MenuItem>
      </Menu>

      {/* Selection checkbox */}
      <Box
        className={`${classes.checkboxContainer} ${
          selected ? classes.checkboxVisible : ""
        } ${hovering ? classes.checkboxVisible : ""}`}
        onClick={handleSelectClick}
      >
        <CustomCheckbox
          checked={selected}
          onChange={handleSelectClick}
          label=""
          style={{ padding: 0, margin: 0 }}
        />
      </Box>

      {/* Card header */}
      <Box className={classes.header}>
        {isEditing ? (
          <Box className={classes.editContainer}>
            <TextField
              value={editedName}
              onChange={handleNameChange}
              onKeyDown={handleNameKeyDown}
              autoFocus
              error={!!nameError}
              helperText={nameError}
              className={classes.editInput}
              variant="standard"
              InputProps={{
                endAdornment: (
                  <Box className={classes.editActions}>
                    <IconButton 
                      size="small" 
                      className={classes.actionIcon}
                      onClick={handleSaveEdit}
                    >
                      <CheckIcon color="primary" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      className={classes.actionIcon}
                      onClick={handleCancelEdit}
                    >
                      <CloseIcon color="error" />
                    </IconButton>
                  </Box>
                ),
              }}
            />
          </Box>
        ) : (
          <Tooltip
            title={`${
              simulation.sid ? `S${String(simulation.sid).padStart(3, "0")} ` : ""
            }${simulation.name || "Untitled Simulation"}`}
            placement="top-start"
          >
            <Typography className={classes.title}>
              {simulation.sid && (
                <span className={classes.simulationId}>
                  S{String(simulation.sid).padStart(3, "0")}&nbsp;&nbsp;
                </span>
              )}
              {simulation.name || "Untitled Simulation"}
            </Typography>
          </Tooltip>
        )}
        {simulation.public === false && !isEditing && (
          <Tooltip title="Private Simulation">
            <Chip
              label="Private"
              size="small"
              className={`${classes.chip} ${classes.privateChip}`}
            />
          </Tooltip>
        )}
      </Box>

      {/* Card content */}
      <CardContent className={classes.contentContainer}>
        {/* Left side: User and date info */}
        <Box className={classes.metaContainer}>
          <Box className={classes.meta}>
            <Box className={classes.infoRow}>
              <Box className={classes.avatarContainer}>
                <Avatar className={classes.avatar}>
                  {getInitials(simulation.createdByName)}
                </Avatar>
              </Box>
              <Typography className={classes.infoText} variant="body2">
                {simulation.createdByName || "Anonymous"}
              </Typography>
            </Box>

            <Box className={classes.infoRow}>
              <ScheduleIcon className={classes.icon} />
              <Typography className={classes.infoText} variant="body2">
                {moment(simulation.createdOn).fromNow()}
              </Typography>

              {typeof simulation.numOfSteps === "number" && (
                <Chip
                  icon={<CodeIcon style={{ fontSize: 14 }} />}
                  label={`${simulation.numOfSteps} Steps`}
                  size="small"
                  className={`${classes.chip} ${classes.stepsChip}`}
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* Right side: Functions used */}
        {simulation.functionsInUse &&
          simulation.functionsInUse.length > 0 && (
            <Box className={classes.functionsContainer}>
              <Typography className={classes.tagsHeading}>
                Functions
              </Typography>
              <Box className={classes.tagsRow}>
                {displayedTags.map((func, idx) => (
                  <Tooltip
                    key={func.name + idx}
                    title={`${func.name} ${
                      func.numOfOccurances > 1
                        ? `(${func.numOfOccurances}×)`
                        : ""
                    }`}
                  >
                    <Chip
                      label={
                        func.name.length > 10
                          ? func.name.substring(0, 10) + "..."
                          : func.name
                      }
                      className={classes.tag}
                      size="small"
                    />
                  </Tooltip>
                ))}
                {hasMoreTags && (
                  <Typography className={classes.moreTags}>
                    +{additionalTagsCount} more
                  </Typography>
                )}
              </Box>
            </Box>
          )}
      </CardContent>
    </Card>
  )
}
